/**
 * O cartão do miolo de Finanças: contorno, cabeçalho, título e corpo.
 *
 * Papel: reproduz `.card`, `.card-header`, `.card-title` e `.card-body`
 * (l. 58–61) — borda bege, raio `--emb-raio`, 16 px abaixo; cabeçalho em
 * `--superficie-2` com `11px 16px` e título em Outfit 14 px 400; corpo com
 * 16 px.
 *
 * Como usar: o cabeçalho põe título e o que vier à direita (o total, o ano do
 * gráfico) nas duas pontas, por `justify-between`. O respiro do corpo muda de
 * tabela para tabela na origem (`padding:8px 0` nas tabelas por categoria) e
 * entra por `className`, aplicado depois do respiro padrão.
 */
import { type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * O contorno do cartão.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `<div>` do cartão.
 */
export function Cartao({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'mb-4 overflow-hidden rounded-emb border border-emb-borda bg-superficie',
        className,
      )}
      {...props}
    />
  );
}

/**
 * O cabeçalho do cartão, em `--superficie-2`.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `<div>` do cabeçalho.
 */
export function CabecalhoDoCartao({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex items-center justify-between border-b border-emb-borda bg-superficie-2 px-4 py-[11px]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * O título do cartão, em Outfit 14 px 400.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `<span>` do título.
 */
export function TituloDoCartao({ className, ...props }: ComponentProps<'span'>) {
  return (
    <span
      className={cn('font-outfit text-[14px] font-normal text-foreground', className)}
      {...props}
    />
  );
}

/**
 * O corpo do cartão.
 *
 * @param className - Classes de quem chama, aplicadas depois do respiro de 16 px.
 * @returns O `<div>` do corpo.
 */
export function CorpoDoCartao({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('p-4', className)} {...props} />;
}
